import { ActionIcon, Badge, Button, Group, Paper, Text } from '@mantine/core';
import { IconEdit, IconEye, IconTrash } from '@tabler/icons-react';
import type { TemplateListItemDTO } from '@/types/messageTemplates';
import { MessageEmailPreview } from '../MessageEmailPreview/MessageEmailPreview';
import styles from './TemplateCard.module.sass';

interface TemplateCardProps {
  template: TemplateListItemDTO;
  onEdit: (templateId: string) => void;
  onEditInDesigner: (templateId: string) => void;
  onDelete: (templateId: string) => void;
  onPreview: (template: TemplateListItemDTO) => void;
  onToggleStatus: (template: TemplateListItemDTO) => void;
  isEditLoading: boolean;
  isDesignerLoading: boolean;
  isDeleteLoading: boolean;
  isStatusUpdating: boolean;
}

export const TemplateCard = ({
  template,
  onEdit,
  onEditInDesigner,
  onDelete,
  onPreview,
  onToggleStatus,
  isEditLoading,
  isDesignerLoading,
  isDeleteLoading,
  isStatusUpdating,
}: TemplateCardProps) => {
  return (
    <Paper className={styles.templateCard} shadow="xs">
      <div className={styles.statusBar}>
        <Group gap="xs" wrap="nowrap">
          <Badge variant="light" color={template.status === 'draft' ? 'yellow' : 'green'} size="sm">
            {template.status}
          </Badge>
          {template.isShared && (
            <Badge variant="light" color="blue" size="sm">
              Shared
            </Badge>
          )}
        </Group>
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            variant="subtle"
            size="md"
            color="gray"
            onClick={() => onEdit(template.id)}
            loading={isEditLoading}
            aria-label="Edit template"
          >
            <IconEdit size={18} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            size="md"
            color="red"
            onClick={() => onDelete(template.id)}
            loading={isDeleteLoading}
            aria-label="Delete template"
          >
            <IconTrash size={18} />
          </ActionIcon>
        </Group>
      </div>

      <div className={styles.header}>
        <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
          <Text className={styles.title} lineClamp={2}>
            {template.title}
          </Text>
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
            {new Date(template.updatedDate).toLocaleDateString()}
          </Text>
        </Group>

        {template.tags.length > 0 ? (
          <Group wrap="wrap" gap="xs" align="center">
            {template.tags.map((tag) => (
              <span key={tag.id} className={styles.tag}>
                {tag.name}
              </span>
            ))}
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            No tags
          </Text>
        )}
      </div>

      <div className={styles.previewArea}>
        <MessageEmailPreview content={template.content} className={styles.previewWrapper} />
      </div>

      <div className={styles.footer}>
        <Group justify="space-between" align="center" w="100%" wrap="wrap">
          <Group gap="xs" wrap="wrap">
            <Button
              variant="light"
              size="sm"
              loading={isDesignerLoading}
              onClick={() => onEditInDesigner(template.id)}
            >
              Edit in Designer
            </Button>
            <Button variant="light" size="sm" leftSection={<IconEye size={16} />} onClick={() => onPreview(template)}>
              Preview
            </Button>
          </Group>
          <Button variant="default" size="sm" loading={isStatusUpdating} onClick={() => onToggleStatus(template)}>
            {template.status === 'draft' ? 'Publish' : 'Move to Draft'}
          </Button>
        </Group>
      </div>
    </Paper>
  );
};
